import { useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from '../../main';
import { toast } from 'react-toastify';
import { createNivel, deleteNivel, updateNivel, fetchNiveles, fetchLevelUsage, reassignLevel } from '../api';
import { useTenantCode } from './useTenantCode';
import { invalidateAfterReassign } from './invalidations';


// Query hook para obtener niveles
export const useNivelesQuery = () => {
    const tenantCode = useTenantCode();
    return useQuery({
        queryKey: ['niveles', tenantCode],
        queryFn: fetchNiveles
    });
};

// Mutaciones para crear niveles llamando a la función correspondiente
export const useCreateNivelMutation = () => useMutation({
    mutationFn: createNivel, 
    onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['niveles'] });
        // Invalidar eventos para actualizar colores en calendario
        queryClient.invalidateQueries({ queryKey: ['eventos'] });
        toast.success('Nivel creado exitosamente');
    },
    onError: () => {
        toast.error('Error creando el nivel');
    },
});


// Mutaciones para actualizar niveles llamando a la función correspondiente
export const useUpdateNivelMutation = () =>  useMutation({
    mutationFn: updateNivel,
    onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['niveles'] });
        // Invalidar eventos para actualizar colores en calendario inmediatamente
        queryClient.invalidateQueries({ queryKey: ['eventos'] });
        toast.success('Nivel actualizado exitosamente');
    },
    onError: () => {
        toast.error('Error actualizando el nivel');
    },
});


// B1 — uso de un nivel: clases, plantillas, plazas activas y recuperaciones.
export const useLevelUsageQuery = (nivelId?: string) => {
    const tenantCode = useTenantCode();
    return useQuery({
        queryKey: ['level-usage', tenantCode, nivelId],
        queryFn: () => fetchLevelUsage(nivelId as string),
        enabled: Boolean(nivelId),
        staleTime: 0,
    });
};

// B1 — reasignar clases futuras, plantillas, plazas y recuperaciones a otro nivel.
export const useReassignLevelMutation = () => {
    const tenantCode = useTenantCode();
    return useMutation({
        mutationFn: ({ nivelId, targetId }: { nivelId: string; targetId: string }) =>
            reassignLevel(nivelId, targetId),
        onSuccess: () => invalidateAfterReassign(tenantCode),
        onError: (error: any) => {
            toast.error(error.response?.data?.message || 'Error reasignando el nivel');
        },
    });
};

// Mutaciones para eliminar niveles llamando a la función correspondiente
// B1: puede acabar archivando en vez de borrando; el mensaje sale de la respuesta.
export const useDeleteNivelMutation = () => {
    const tenantCode = useTenantCode();
    return useMutation({
        mutationFn: deleteNivel,
        onSuccess: (response) => {
            // Incluye ['eventos'], que este hook no invalidaba aunque crear y
            // actualizar sí lo hacen: los colores del calendario salen del nivel.
            invalidateAfterReassign(tenantCode);
            toast.success(response?.archived ? 'Nivel archivado' : 'Nivel eliminado');
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.message || 'Error eliminando el nivel');
        },
    });
};